import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CContainer, CSpinner, CBadge } from '@coreui/react';
import { Lock, Unlock, ChevronRight } from 'lucide-react';
import api from '../../api/axiosInstance';

// 🌟 MathRenderer import karein (jo ViewQuestion.js me used h)
import MathRenderer from '../../components/MathRenderer'; 

const ExerciseQuestions = () => {
  const { category, chapter, exerciseNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exerciseName, setExerciseName] = useState('');

  const isAuthenticated = !!(localStorage.getItem('authToken') || localStorage.getItem('token'));

  useEffect(() => {
    const fetchExerciseQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint = `/api/content/GetExerciseQuestions/${encodeURIComponent(category)}/${encodeURIComponent(chapter)}/${exerciseNo}`;
        const res = await api.get(endpoint, { skipAuthCheck: true });

        setQuestions(res.data?.questions || res.data || []);
        setExerciseName(res.data?.exerciseName || '');
      } catch (err) {
        console.error("Error fetching exercise questions:", err);
        setError("Failed to load questions for this exercise.");
      } finally {
        setLoading(false);
      }
    };

    fetchExerciseQuestions();
  }, [category, chapter, exerciseNo]);

  const handleQuestionClick = (e, question) => {
    const effectiveIsLocked = isAuthenticated ? false : question.isLocked;

    if (effectiveIsLocked) {
      e.preventDefault();
      navigate('/login', { state: { from: location.pathname } });
    } else {
      const qText = question.questionText || question.QuestionText || '';
      if (!question.customUrl) {
        const cleanText = qText
          .replace(/<[^>]*>?/gm, '')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        const slugBase = cleanText
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, '');

        const finalSlug = slugBase || 'question';
        navigate(`/content/view-question/${question.id}/${finalSlug.substring(0,50)}`);
      } else {
        navigate(`/content/view-question/${question.id}/${question.customUrl}`);
      }
    }
  };

  const formattedCategory = category ? category.replace(/-/g, ' ') : '';
  const formattedChapter = chapter ? chapter.replace(/-/g, ' ') : '';

  if (loading) {
    return (
      <CContainer className="text-center py-5 my-5">
        <CSpinner color="primary" size="lg" />
        <p className="mt-3 text-dark fw-bold">Loading Questions...</p>
      </CContainer>
    );
  }

  if (error) {
    return (
      <CContainer className="py-5 text-center">
        <div className="alert alert-danger shadow-sm rounded-3 py-3 fw-semibold">
          {error}
        </div>
      </CContainer>
    );
  }

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* BANNER */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-4" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <span className="text-warning fw-bold text-uppercase" style={{ fontSize: '13px', letterSpacing: '1px' }}>
            EXERCISE PRACTICE MODULE
          </span>
          <h1 className="fw-bold m-0 mt-1 display-6 text-capitalize" style={{ color: 'yellow' }}>
            {formattedCategory} {formattedChapter}
          </h1>
          <p className="text-light opacity-90 m-0 fs-5 mt-2 fw-medium">
            {exerciseName || `Exercise ${exerciseNo}`}
          </p>
          <div className="mt-3">
            <CBadge color="light" className="text-primary px-3 py-2 fs-6 rounded-pill fw-bold">
              Total Questions: {questions.length}
            </CBadge>
          </div>
        </div>
      </div>

      {/* QUESTIONS LIST */}
      <CContainer className="pb-5" style={{ maxWidth: '1140px' }}>
        <div className="d-flex flex-column gap-3 mt-2">
          {questions.length > 0 ? (
            questions.map((q, idx) => {
              const lockedState = isAuthenticated ? false : q.isLocked;
              const content = q.questionText || q.QuestionText || q.title || '';

              return (
                <div
                  key={q.id || idx}
                  className={`question-card d-flex align-items-start gap-3 p-3 rounded-3 border bg-white shadow-xs cursor-pointer transition-all ${
                    lockedState ? 'border-secondary-subtle' : 'hover-shadow'
                  }`}
                  onClick={(e) => handleQuestionClick(e, q)}
                >
                  <div className="d-flex flex-column align-items-center gap-2">
                    <div 
                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                      style={{ width: '32px', height: '32px', fontSize: '0.9rem' }}
                    >
                      {idx + 1}
                    </div>
                    {lockedState ? (
                      <Lock size={16} className="text-secondary" />
                    ) : (
                      <Unlock size={16} className="text-success" />
                    )}
                  </div>

                  {/* 🌟 ViewQuestion wali MathRenderer component ka upyog */}
                  <div className="flex-grow-1 pt-1 overflow-hidden">
                    <div 
                      className="question-text fw-medium text-dark ck-content"
                      style={{ fontSize: '1.05rem', lineHeight: '1.6', color: '#1e293b' }}
                    >
                      <MathRenderer htmlContent={content} />
                    </div>
                  </div>

                  <div className="align-self-center ms-2">
                    {lockedState ? (
                      <CBadge color="secondary" className="px-2 py-1 rounded-pill text-dark border bg-light fw-bold">
                        Locked
                      </CBadge>
                    ) : (
                      <ChevronRight size={20} className="text-primary" />
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="alert alert-info text-center rounded-3 p-4 fw-bold">
              No questions available for this exercise.
            </div>
          )}
        </div>

        <style>{`
          .cursor-pointer { cursor: pointer; }
          .question-card {
            border: 1px solid #cbd5e1 !important;
            transition: all 0.2s ease-in-out;
          }
          .question-card:hover {
            border-color: #0d6efd !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06) !important;
          }
          .question-text img {
            max-width: 100%;
            height: auto;
            display: block;
            margin-top: 10px;
          }
        `}</style>
      </CContainer>
    </div>
  );
};

export default ExerciseQuestions;